// AUTO-WA-39 broadcasts: pure helpers shared by the list, composer and detail.

export const BROADCAST_STATUS = {
  scheduled: { label: 'Scheduled', tone: 'info' },
  sending: { label: 'Sending', tone: 'live' },
  completed: { label: 'Sent', tone: 'live' },
  cancelled: { label: 'Stopped', tone: 'draft' },
};

export const RECIPIENT_STATUS = {
  pending: { label: 'Waiting', tone: 'draft' },
  sending: { label: 'Sending', tone: 'info' },
  sent: { label: 'Sent', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'live' },
  read: { label: 'Read', tone: 'live' },
  failed: { label: 'Failed', tone: 'failed' },
  uncertain: { label: 'Check chat', tone: 'warning' },
  skipped: { label: 'Skipped', tone: 'draft' },
  cancelled: { label: 'Not sent', tone: 'draft' },
};

export const RECIPIENT_FILTERS = [
  { id: 'all', label: 'Everyone' },
  { id: 'read', label: 'Read' },
  { id: 'replied', label: 'Replied' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'pending', label: 'Waiting' },
  { id: 'failed', label: 'Failed' },
];

export const SOURCES = [
  { value: 'first_name', label: 'First name', needsFallback: true },
  { value: 'name', label: 'Full name', needsFallback: true },
  { value: 'phone', label: 'Phone number' },
  { value: 'text', label: 'Same text for everyone' },
];

export const rate = (part, whole) => (Number(whole) > 0 ? Math.round((Number(part) / Number(whole)) * 100) : null);

export const progress = (stats = {}) => {
  const total = Number(stats.total) || 0;
  const done = total - (Number(stats.pending) || 0);
  return total ? Math.min(100, Math.round((done / total) * 100)) : 0;
};

export function audienceLabel(audience = {}) {
  const names = (audience.tags || []).map((tag) => tag.name);
  const except = (audience.excluded_tags || []).map((tag) => tag.name);
  let label = 'Everyone on this number';
  if (audience.mode === 'contacts') label = `${audience.contact_count || 0} chosen ${audience.contact_count === 1 ? 'contact' : 'contacts'}`;
  if (audience.mode === 'tags') {
    const joiner = audience.match === 'all' ? ' and ' : ' or ';
    label = names.length ? `Tagged ${names.join(joiner)}` : 'Tagged contacts';
  }
  return except.length ? `${label}, except ${except.join(', ')}` : label;
}

const VARIABLE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

export const componentOf = (template, type) => (template?.components || []).find((component) => String(component.type).toUpperCase() === type);

// What one slot shows in the preview: the fixed text, or a sample for a contact field.
export function sampleValue(rule, sampleName = 'Asha Rao', samplePhone = '+91 98765 43210') {
  if (!rule) return null;
  if (rule.source === 'text') return rule.value || null;
  if (rule.source === 'phone') return samplePhone;
  const name = String(sampleName || '').trim();
  if (!name) return rule.fallback || null;
  return rule.source === 'first_name' ? name.split(/\s+/)[0] : name;
}

// Template text with each {{key}} replaced; missing values stay visible as {{key}}.
export function fillText(text = '', prefix, variables = {}, sample) {
  return String(text).replace(VARIABLE, (whole, key) => sampleValue(variables[`${prefix}.${key}`], sample?.name, sample?.phone) || whole);
}

// Sensible starting values: the first message value is the first name.
export function defaultVariables(slots = []) {
  const firstText = slots.find((slot) => slot.section === 'body' && slot.kind === 'text');
  return Object.fromEntries(slots.map((slot) => [slot.slot, slot === firstText
    ? { source: 'first_name', value: '', fallback: 'there' }
    : { source: 'text', value: '', fallback: '' }]));
}

// Client-side checks that mirror BroadcastMessage::errors on the server.
export function variableErrors(slots = [], variables = {}) {
  const errors = {};
  slots.forEach((slot) => {
    const rule = variables[slot.slot];
    const needsText = slot.kind !== 'text';
    if (!rule?.source) errors[slot.slot] = 'Choose what fills this.';
    else if (needsText && rule.source !== 'text') errors[slot.slot] = 'Use fixed text here.';
    else if (rule.source === 'text' && !String(rule.value || '').trim()) errors[slot.slot] = 'Enter the text.';
    else if (slot.key === 'media' && !/^https:\/\/\S+$/i.test(String(rule.value || '').trim())) errors[slot.slot] = 'Use a public https:// link.';
    else if (['name', 'first_name'].includes(rule.source) && !String(rule.fallback || '').trim()) errors[slot.slot] = 'Add a fallback for people without a name.';
  });
  return errors;
}

export function audiencePayload(draft) {
  const audience = { mode: draft.mode };
  if (draft.mode === 'tags') { audience.tag_ids = draft.tagIds; audience.match = draft.match; }
  if (draft.mode === 'contacts') audience.contact_ids = draft.contactIds;
  if (draft.excludeTagIds.length) audience.exclude_tag_ids = draft.excludeTagIds;
  return audience;
}

export const audienceReady = (draft) => (draft.mode === 'all')
  || (draft.mode === 'tags' && draft.tagIds.length > 0)
  || (draft.mode === 'contacts' && draft.contactIds.length > 0);

// Meta messaging tiers: how many people a business can start chats with per day.
const TIERS = { TIER_50: 50, TIER_250: 250, TIER_1K: 1000, TIER_2K: 2000, TIER_10K: 10000, TIER_100K: 100000 };
export const tierLimit = (tier) => TIERS[String(tier || '').toUpperCase()] || null;

// A value for <input type="datetime-local"> in the viewer's time zone.
export function localInputValue(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export const newIdempotencyKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `b-${Date.now()}-${Math.random().toString(36).slice(2)}`);

// A scheduled broadcast whose time has passed: the sender picks it up within a minute or two.
export const isOverdue = (broadcast, now = Date.now()) => broadcast?.status === 'scheduled'
  && Boolean(broadcast.scheduled_at) && new Date(broadcast.scheduled_at).getTime() <= now;

export const peopleCount = (count) => `${Number(count || 0).toLocaleString('en-IN')} ${Number(count) === 1 ? 'person' : 'people'}`;

// Meta's delivery errors, in plain words. Unknown ones are shown as Meta wrote them.
const META_ERRORS = [
  [/part of an experiment|130472/i, 'Meta held this marketing message back because this person is in a small WhatsApp test group. You aren’t charged for it.'],
  [/healthy ecosystem|131049/i, 'Meta didn’t deliver it to protect this person from too many marketing messages. Try again another day. You aren’t charged.'],
  [/re-engagement|131047/i, 'More than 24 hours have passed since this person last wrote, so only a template can reach them.'],
  [/undeliverable|131026/i, 'WhatsApp couldn’t deliver it. The number may not use WhatsApp, or their app may be out of date.'],
  [/payment|131042/i, 'Meta refused it because of a payment problem. Check the payment method in Meta’s Billing Hub.'],
];
export const friendlyError = (error) => {
  if (!error) return error;
  const hit = META_ERRORS.find(([pattern]) => pattern.test(error));
  return hit ? hit[1] : error;
};
